/**
 * @return Returns the inferred type of the elements of the provided array.
 */
export type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[]
  ? ElementType
  : never;

/**
 * @return Returns T with all its properties optional, recursively.
 * Works well for objects right now, but might need some changes if needed for other types (such as arrays).
 */
export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends object ? RecursivePartial<T[P]> : T[P];
};

/**
 * @return Returns T with all its properties NonNullable, recursively.
 * Works well for objects right now, but might need some changes if needed for other types (such as arrays).
 */
export type RecursiveNonNullable<T> = {
  [P in keyof T]: T[P] extends object ? RecursiveNonNullable<T[P]> : NonNullable<T[P]>;
};

export type Implements<T, U extends T> = U;

export type KeyOfUnion<T> = T extends T ? keyof T : never;

export type StyledProps<T, K extends keyof T = keyof T> = {
  [P in K as `$${string & P}`]: T[P];
};

// This function is only used to cast err as a Record type.
const hasMessageProperty = (err: unknown): err is Record<string, string> =>
  !!err && typeof err === 'object' && 'message' in err;

export const toError = (err: unknown): Error => {
  if (err instanceof Error) return err;

  /**
   * Stripe errors are error-like objects, but they are not created from the Error constructor.
   * Because of that, `err instanceof Error` returns false, but still this function should return them.
   * This condition makes sure that we return an error-like object from any object containing a "message"
   * string property.
   */
  // For now we just set the message on the error: do we need to collect something else here?
  if (hasMessageProperty(err)) return new Error(err.message);

  return new Error(JSON.stringify(err));
};

export const reverseRecord = <K extends PropertyKey, V extends PropertyKey>(record: Record<K, V>): Record<V, K> =>
  Object.fromEntries(Object.entries(record).map(([key, value]) => [value, key])) as Record<V, K>;
